import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Buildings, ArrowsClockwise, Printer, MagnifyingGlass, FileText } from "@phosphor-icons/react";

function StatusPill({ status }) {
  const map = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    rejected: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <span className={`inline-flex px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border rounded-sm ${map[status] || "text-slate-600 bg-slate-50 border-slate-200"}`}>
      {status}
    </span>
  );
}

export default function TableOfContents() {
  const { user } = useAuth();
  const [toc, setToc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/toc");
      setToc(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!toc) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return toc;
    return {
      ...toc,
      sections: toc.sections
        .map((s) => ({
          ...s,
          policies: s.policies.filter((p) =>
            (p.policy_name || "").toLowerCase().includes(needle) ||
            (p.policy_number || "").toLowerCase().includes(needle) ||
            (s.department_name || "").toLowerCase().includes(needle)
          ),
        }))
        .filter((s) => s.policies.length > 0),
    };
  }, [toc, q]);

  const totalShown = useMemo(
    () => (filtered?.sections || []).reduce((n, s) => n + s.policies.length, 0),
    [filtered]
  );

  return (
    <div className="space-y-6" data-testid="toc-page">
      {/* Print-only stylesheet — hide chrome so browser Print shows the clean policy index */}
      <style>{`
        @media print {
          aside, header, .no-print { display: none !important; }
          main > div { padding: 0 !important; max-width: 100% !important; }
          .card-flat { border: none !important; page-break-inside: avoid; }
          .toc-section { page-break-before: auto; }
        }
      `}</style>

      <div className="flex items-start justify-between gap-4 no-print">
        <div>
          <div className="label-caps">Master index</div>
          <h1 className="font-display text-3xl text-navy uppercase mt-1">Table of Contents</h1>
          <p className="text-sm text-slate-500 mt-1">
            Department-wise policy index. Policy number & name are auto-extracted from each document.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <>
              <button
                onClick={async () => {
                  if (!window.confirm(
                    "APPLY RADISH STANDARD SETUP\n\n" +
                    "This will:\n" +
                    "  1. Set watermarks on all 9 departments (Finance / HR / QA / R&D / etc.)\n" +
                    "  2. Reindex every doc's policy number + name from its body\n" +
                    "  3. Regenerate every cached preview PDF (runs in background)\n\n" +
                    "All users will automatically see the updated watermarks + policy numbers " +
                    "on next open — no further action needed.\n\nContinue?"
                  )) return;
                  try {
                    const { toast } = await import("sonner");
                    toast.loading("Applying standard setup…", { id: "apply-std" });
                    const { data } = await api.post("/setup/apply-standard-watermarks");
                    toast.success(
                      `Watermarks: ${data.watermarks_applied}/9 · Policies reindexed: ${data.reindex_updated}/${data.reindex_scanned} · Cache regenerating in background (~2 min)`,
                      { id: "apply-std", duration: 8000 }
                    );
                    await load();
                  } catch {
                    const { toast } = await import("sonner");
                    toast.error("Setup failed — see console", { id: "apply-std" });
                  }
                }}
                data-testid="toc-apply-standard"
                className="btn-navy inline-flex items-center gap-2"
                title="One-click: apply Radish's standard watermarks + reindex + regenerate cache for ALL users"
              >
                ⚡ Apply Standard Setup
              </button>
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post("/toc/reindex");
                    const { toast } = await import("sonner");
                    toast.success(`Reindexed ${data.updated}/${data.scanned} documents`);
                    await load();
                  } catch {
                    const { toast } = await import("sonner");
                    toast.error("Reindex failed");
                  }
                }}
                data-testid="toc-reindex"
                className="btn-outline-navy inline-flex items-center gap-2"
                title="Re-scan every document body to refresh policy number + name"
              >
                <ArrowsClockwise size={14} weight="bold" /> Reindex bodies
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Purge every cached preview PDF? Next preview of each doc will re-render from source with the latest fonts + watermark. This is safe — nothing is deleted from the source library.")) return;
                  try {
                    const { data } = await api.post("/toc/purge-preview-cache");
                    const { toast } = await import("sonner");
                    toast.success(`Regenerated ${data.regenerated} cached previews (${data.skipped} skipped, ${data.errors} errors)`);
                  } catch {
                    const { toast } = await import("sonner");
                    toast.error("Purge failed");
                  }
                }}
                data-testid="toc-purge-cache"
                className="btn-outline-navy inline-flex items-center gap-2"
                title="Delete every cached preview PDF so next open re-renders with latest fonts + watermark"
              >
                <ArrowsClockwise size={14} weight="bold" /> Purge cache
              </button>
            </>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="toc-refresh"
            className="btn-outline-navy inline-flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={() => window.print()}
            data-testid="toc-print"
            className="btn-navy inline-flex items-center gap-2"
          >
            <Printer size={14} weight="fill" /> Print
          </button>
        </div>
      </div>

      {/* Summary + search */}
      <div className="card-flat p-4 flex items-center justify-between gap-4 no-print">
        <div className="flex items-center gap-6 text-sm">
          <div>
            <div className="label-caps mute">Departments</div>
            <div className="font-display text-2xl text-navy mt-1" data-testid="toc-total-depts">
              {filtered?.total_departments ?? "—"}
            </div>
          </div>
          <div>
            <div className="label-caps mute">Policies indexed</div>
            <div className="font-display text-2xl text-navy mt-1" data-testid="toc-total-policies">
              {totalShown}
              {q && toc && totalShown !== toc.total_policies && (
                <span className="text-sm text-slate-400 font-sans font-normal ml-2">
                  / {toc.total_policies}
                </span>
              )}
            </div>
          </div>
          {toc?.generated_at && (
            <div>
              <div className="label-caps mute">Generated</div>
              <div className="text-xs font-mono text-slate-600 mt-1.5">
                {new Date(toc.generated_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
        <div className="relative w-72">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by number, name, department…"
            data-testid="toc-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:border-cyan"
          />
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-4">
        <h1 className="font-display text-3xl text-navy uppercase">Document Hub — Table of Contents</h1>
        <div className="text-xs font-mono text-slate-600 mt-1">
          Generated {toc?.generated_at && new Date(toc.generated_at).toLocaleString()} · {totalShown} policies across {filtered?.total_departments || 0} departments
        </div>
      </div>

      {loading && !toc && (
        <div className="card-flat p-8 text-center text-slate-500 text-sm">Loading policy index…</div>
      )}

      {filtered && filtered.sections.length === 0 && (
        <div className="card-flat p-10 text-center">
          <FileText size={40} weight="duotone" className="text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-500">
            {q ? `No policies match "${q}".` : "No policies uploaded yet."}
          </div>
        </div>
      )}

      {filtered?.sections.map((sec) => (
        <div key={sec.department_id || "unassigned"} className="card-flat toc-section" data-testid={`toc-section-${sec.department_id || "unassigned"}`}>
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Buildings size={16} weight="duotone" className="text-cyan" />
              <span className="font-display text-lg text-navy uppercase">{sec.department_name}</span>
            </div>
            <div className="label-caps mute" data-testid={`toc-count-${sec.department_id || "unassigned"}`}>
              {sec.policies.length} policies
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left py-2.5 px-5 w-10">#</th>
                <th className="text-left py-2.5 px-3 w-56">Policy Number</th>
                <th className="text-left py-2.5 px-3">Policy Name</th>
                <th className="text-left py-2.5 px-3 w-24 no-print">Status</th>
                <th className="text-left py-2.5 px-3 w-16 no-print">v</th>
              </tr>
            </thead>
            <tbody>
              {sec.policies.map((p, i) => (
                <tr key={p.id} className="dms-row border-b border-slate-100 last:border-b-0">
                  <td className="py-2.5 px-5 text-xs font-mono text-slate-400">{i + 1}</td>
                  <td className="py-2.5 px-3 text-xs font-mono text-navy whitespace-nowrap">
                    {p.policy_number || <span className="text-slate-300 italic">—</span>}
                  </td>
                  <td className="py-2.5 px-3">
                    <Link
                      to={`/documents/${p.id}`}
                      className="text-navy hover:text-cyan hover:underline"
                      data-testid={`toc-link-${p.id}`}
                    >
                      {p.policy_name || "(untitled)"}
                    </Link>
                  </td>
                  <td className="py-2.5 px-3 no-print">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="py-2.5 px-3 text-xs font-mono text-slate-500 no-print">
                    v{p.current_version}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
